import { apiRequest } from "../client";
import type { ApiResponse } from "../types";

// Re-export X-Ray API
export * from "./xray";

export interface PortfolioFieldData {
  portfolio_id: string;
  fundamental_data: {
    [key: string]: {
      current_value: any;
      overrides: FieldOverride[];
    };
  };
  exposures: {
    [key: string]: {
      current_value: any;
      overrides: FieldOverride[];
      options?: string[];
    };
  };
  custom_fields: Array<{
    field_definition_id: string;
    field_name: string;
    field_type: string;
    options?: string[];
    current_value: any;
    overrides: FieldOverride[];
  }>;
}

export interface FieldOverride {
  value: any;
  valid_from?: string;
  valid_to?: string;
  created_by: string;
  updated_by: string;
  created_at?: string;
  updated_at?: string;
  client_portfolio_data_id: string;
}

export interface CreateOverrideRequest {
  portfolio_id: string;
  field_path: string;
  value: any;
  valid_from?: string;
  valid_to?: string;
  skip_refresh?: boolean;
}

export interface CreateOverrideResponse {
  client_portfolio_data_id: string;
  message: string;
  snapshot_task_ids?: string[];
}

/**
 * Get all fields for a portfolio including current values and overrides
 */
export async function getPortfolioFields(
  portfolioId: string
): Promise<PortfolioFieldData> {
  return apiRequest<PortfolioFieldData>({
    method: "GET",
    url: `/portfolios/portfolio-fields/${portfolioId}`,
  });
}

/**
 * Create or update a portfolio field override
 */
export async function createPortfolioFieldOverride(
  request: CreateOverrideRequest
): Promise<CreateOverrideResponse> {
  return apiRequest<CreateOverrideResponse>({
    method: "POST",
    url: "/portfolios/override-field",
    data: request,
  });
}

/**
 * Delete a portfolio field override
 */
export async function deletePortfolioFieldOverride(
  overrideId: string
): Promise<{ message: string }> {
  return apiRequest<{ message: string }>({
    method: "DELETE",
    url: `/portfolios/override-field/${overrideId}`,
  });
}

export interface BenchmarkOption {
  child_benchmark_id: string;
  display_name: string;
  benchmark_type: 'benchmark' | 'product';
  benchmark_object: {
    benchmark_id: string;
    benchmark_type: 'benchmark' | 'product';
    name: string;
  };
  metadata?: {
    isin?: string;
    currency?: string;
  };
}

export interface BenchmarkPermissionsResponse {
  benchmarks: BenchmarkOption[];
}

/**
 * Get all benchmark permissions for the current user's company
 */
export async function getBenchmarkPermissions(): Promise<BenchmarkPermissionsResponse> {
  return apiRequest<BenchmarkPermissionsResponse>({
    method: "GET",
    url: "/portfolios/benchmark-permissions",
  });
}

export interface PortfolioPermissions {
  can_delete: boolean;
}

/**
 * Action permissions for the current user on a portfolio.
 */
export async function getPortfolioPermissions(
  portfolioId: string
): Promise<PortfolioPermissions> {
  return apiRequest<PortfolioPermissions>({
    method: "GET",
    url: `/portfolios/${portfolioId}/permissions`,
  });
}

/**
 * Permanently delete a portfolio and related data.
 */
export async function deletePortfolio(
  portfolioId: string
): Promise<{ message: string; portfolio_id: string; status: string }> {
  return apiRequest({
    method: "DELETE",
    url: `/portfolios/${portfolioId}`,
  });
}

export interface SimplePortfolio {
  portfolio_id: string;
  name: string;
  status?: string;
  client_id?: string;
}

/**
 * Fetch simple portfolio list (lightweight endpoint for search/selection)
 */
export async function fetchSimplePortfolios(params?: {
  status?: string;
  client_id?: string;
}): Promise<SimplePortfolio[]> {
  return apiRequest<SimplePortfolio[]>({
    method: "GET",
    url: "/portfolios/simple",
    params,
  });
}

export interface CompanyPortfolio {
  portfolio_id: string;
  name: string;
  description?: string | null;
  status?: string;
  type?: string;
  inception_date?: string | null;
  portfolio_classification?: "model" | "actual" | null;
}

/**
 * List the portfolios belonging to a company, sorted alphabetically by name.
 * Drafts are excluded — they are what-if scratch portfolios, not real ones.
 */
export async function fetchCompanyPortfolios(
  companyId?: string
): Promise<CompanyPortfolio[]> {
  const response = await apiRequest<CompanyPortfolio[]>({
    method: "GET",
    url: "/portfolios/",
    params: companyId ? { client_id: companyId } : undefined,
  });

  return response
    .filter((portfolio) => portfolio.type !== "draft")
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );
}

export interface PortfolioFeeSettings {
  amount: number;
  currency?: string | null;
  frequency: string;
}

/** The "Basic Settings" of a portfolio, as entered in the Portfolio Builder. */
export interface PortfolioSettings {
  portfolio_id: string;
  name: string;
  description?: string | null;
  base_currency: string;
  inception_date: string; // YYYY-MM-DD
  rebalancing_frequency: string;
  portfolio_classification: "model" | "actual";
  fees: PortfolioFeeSettings;
}

export type PortfolioSettingsUpdate = Partial<
  Omit<PortfolioSettings, "portfolio_id">
>;

export interface UpdatePortfolioSettingsResponse {
  portfolio_id: string;
  updated_fields: string[];
  /** Always true — saving settings always triggers a full recalculation. */
  recalculation_queued: boolean;
  task_id: string;
  status_url: string;
  settings: PortfolioSettings;
}

export async function getPortfolioSettings(
  portfolioId: string
): Promise<PortfolioSettings> {
  return apiRequest<PortfolioSettings>({
    method: "GET",
    url: `/portfolios/${portfolioId}/settings`,
  });
}

/**
 * Update a portfolio's basic settings. Holdings are untouched. A full
 * recalculation is always queued server-side and its task_id returned.
 */
export async function updatePortfolioSettings(
  portfolioId: string,
  update: PortfolioSettingsUpdate
): Promise<UpdatePortfolioSettingsResponse> {
  return apiRequest<UpdatePortfolioSettingsResponse>({
    method: "PATCH",
    url: `/portfolios/${portfolioId}/settings`,
    data: update,
  });
}

export interface DuplicatePortfolioResponse {
  portfolio_id: string;
  name: string;
  source_portfolio_id: string;
  status: string;
  task_id: string;
  status_url: string;
  message: string;
}

/**
 * Duplicate a portfolio under a new name. Settings, holdings and trades are
 * copied; the copy gets a freshly generated portfolio_id and a full
 * recalculation is queued server-side.
 */
export async function duplicatePortfolio(
  portfolioId: string,
  name: string,
  description?: string
): Promise<DuplicatePortfolioResponse> {
  return apiRequest<DuplicatePortfolioResponse>({
    method: "POST",
    url: `/portfolios/${portfolioId}/duplicate`,
    data: { name, ...(description !== undefined ? { description } : {}) },
  });
}

// ============================================================================
// Holdings Types
// ============================================================================

export interface PortfolioHolding {
  weight: number; // Percentage
  resource_id: string;
  resource_name: string | null;
  holding_date: string | null;
  currency: string | null;
  isin: string | null;
  reconciliation_type: string;
}

export interface PortfolioHoldingsResponse {
  portfolio: {
    id: string;
    name: string;
    status: string;
    created_on: string | null;
    created_by: string | null;
    updated_on: string | null;
  };
  holdings: PortfolioHolding[];
  total_value: number | null;
  holdings_date: string | null;
}

/**
 * Get holdings for a portfolio
 */
export async function getPortfolioHoldings(
  portfolioId: string,
  options?: {
    date?: string;
    includeDrafts?: boolean;
    modelOnly?: boolean;
  }
): Promise<PortfolioHoldingsResponse> {
  const params: Record<string, string | boolean> = {};
  if (options?.date) params.date = options.date;
  if (options?.includeDrafts !== undefined) params.include_drafts = options.includeDrafts;
  if (options?.modelOnly !== undefined) params.model_only = options.modelOnly;

  return apiRequest<PortfolioHoldingsResponse>({
    method: "GET",
    url: `/portfolios/${portfolioId}/holdings`,
    params,
  });
}

export interface EnrichedHoldingItem {
  child_universe_id?: string;
  portfolio_reference_id?: string;
  resource_id?: string;
  resource_type?: "product" | "portfolio" | "benchmark";
  holding_type?: "PRODUCT" | "PORTFOLIO";
  weight?: number;
  value?: number;
  value_product_currency?: number;
  units?: number;
  display_name?: string;
  isin?: string;
  currency?: string;
}

export interface PortfolioHoldingsRecord {
  _id: string;
  portfolio_id: string;
  as_at_date: string;
  reconciliation_type: "MANUAL" | "DRAFT";
  holding_input_type?:
    | "weight_based_model"
    | "weight_based_actual"
    | "value_based"
    | "units_based"
    | "value_product_currency_based";
  holdings_type?: "TARGET";
  holdings: EnrichedHoldingItem[];
  total_value?: number;
  created_at: string;
  created_by: string;
}

interface AllHoldingsPageResponse {
  data: PortfolioHoldingsRecord[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface EditableHoldingPayload {
  child_universe_id?: string;
  portfolio_reference_id?: string;
  resource_id?: string;
  resource_type?: "product" | "portfolio" | "benchmark";
  weight: number;
}

export interface ValidateHoldingsResponse {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

export interface UpdateHoldingsRecordResponse {
  message: string;
  holdings_id: string;
  holdings_count: number;
  total_weight: number;
  reconciliation_type: "MANUAL" | "DRAFT";
  holdings_type?: "TARGET";
  recalculation?: "triggered";
  task_id?: string;
}

/**
 * Fetch the latest live MANUAL (non-TARGET) holdings record with enriched
 * product/portfolio/benchmark display names.
 */
export async function getLatestManualHoldingsRecord(
  portfolioId: string
): Promise<PortfolioHoldingsRecord | null> {
  let pageNum = 1;
  let totalPages = 1;

  while (pageNum <= totalPages) {
    const page = await apiRequest<AllHoldingsPageResponse>({
      method: "GET",
      url: `/portfolios/${portfolioId}/holdings/all`,
      params: { page: pageNum, page_size: 100 },
    });

    const records = page.data ?? [];
    const manualRecord = records.find(
      (record) =>
        record.reconciliation_type === "MANUAL" &&
        record.holdings_type !== "TARGET",
    );

    if (manualRecord) {
      return apiRequest<PortfolioHoldingsRecord>({
        method: "GET",
        url: `/portfolios/${portfolioId}/holdings/${manualRecord._id}`,
      });
    }

    totalPages = page.pagination?.total_pages ?? 1;
    pageNum += 1;
  }

  return null;
}

export async function validatePortfolioHoldings(
  portfolioId: string,
  holdings: EditableHoldingPayload[]
): Promise<ValidateHoldingsResponse> {
  return apiRequest<ValidateHoldingsResponse>({
    method: "POST",
    url: `/portfolios/${portfolioId}/holdings/validate`,
    data: holdings,
  });
}

export async function updatePortfolioHoldingsRecord(
  portfolioId: string,
  holdingsId: string,
  holdings: EditableHoldingPayload[],
  options?: {
    skipRefresh?: boolean;
    asAtDate?: string;
  }
): Promise<UpdateHoldingsRecordResponse> {
  const params: Record<string, string> = {
    skip_refresh: String(Boolean(options?.skipRefresh)),
    save_as_draft: "false",
    holdings_type: "",
  };
  if (options?.asAtDate) {
    params.as_at_date = options.asAtDate;
  }

  return apiRequest<UpdateHoldingsRecordResponse>({
    method: "PUT",
    url: `/portfolios/${portfolioId}/holdings/${holdingsId}`,
    params,
    data: holdings,
  });
}

